// The central lifecycle reducer. Pure: (state, action) → state. Every mutation to
// the persistent lifecycle data flows through here; computed values are derived
// separately (compute.ts) so they stay reactive. IDs are supplied by the caller
// (the store generates them) to keep this reducer pure and deterministic.

import type { LifeAsset, LifeLiability, LifeState, Mentor, Mindset, Quadrant, VisionItem, KnowledgeId, LearningAnswer } from './types.js';
import { deriveLife, isVisionAchieved, visionBracket } from './compute.js';

export type LifeAction =
  | { type: 'RESET'; state: LifeState }
  | { type: 'SET_AGE'; age: number }
  | { type: 'ADVANCE_YEAR' }
  | { type: 'ADJUST_CASH'; delta: number }
  | { type: 'SET_CASH'; value: number }
  | { type: 'SET_LIVING_COSTS'; value: number }
  | { type: 'ADD_ASSET'; asset: LifeAsset }
  | { type: 'BUY_ASSET'; asset: LifeAsset } // deducts asset.value from cash, then adds it
  | { type: 'REMOVE_ASSET'; id: string }
  | { type: 'ADD_LIABILITY'; liability: LifeLiability }
  | { type: 'REMOVE_LIABILITY'; id: string }
  | { type: 'ADJUST_MINDSET'; deltas: Partial<Mindset> }
  | { type: 'SET_MINDSET'; mindset: Mindset }
  | { type: 'SET_QUADRANT'; quadrant: Quadrant }
  | { type: 'ADD_VISION'; item: VisionItem }
  | { type: 'ACHIEVE_VISION'; id: string }
  | { type: 'REMOVE_VISION'; id: string }
  | { type: 'ADD_MENTOR'; mentor: Mentor }
  | { type: 'REMOVE_MENTOR'; id: string }
  // ── bridge from the weekly business sim ──
  | { type: 'SET_HARVESTED_WEEK'; week: number } // mark weekly profit swept in
  | { type: 'DEPOSIT_TO_SAVINGS'; amount: number; monthlyRate: number } // grow the Savings asset
  // ── vision board ──
  | { type: 'MARK_VISION_PROMPTED' } // record that the board was set for this age bracket
  // ── time ──
  | { type: 'END_OF_YEAR' } // advance one year: age+1, settle a year of asset cashflow, re-baseline
  // ── knowledge check ──
  | { type: 'MARK_KC_TRIGGERED'; id: KnowledgeId } // fired-once flag, set before asking
  | { type: 'RECORD_KC_ANSWER'; answer: LearningAnswer } // store the player's answer
  // ── mastermind ──
  | { type: 'MARK_MENTOR_READ'; week: number } // clears the cottage glow until next week
  // ── onboarding ──
  | { type: 'MARK_FIRST_STAND_TAP' } // first ever tap of the stand — clears the pulse hint
  | { type: 'SET_PLAYER_NAME'; name: string }; // first-run name entry (trimmed, capped; local-only)

const clamp01to100 = (n: number) => Math.min(100, Math.max(0, n));
const clampAge = (n: number) => Math.min(18, Math.max(8, n));

function adjustMindset(m: Mindset, d: Partial<Mindset>): Mindset {
  return {
    desire: clamp01to100(m.desire + (d.desire ?? 0)),
    faith: clamp01to100(m.faith + (d.faith ?? 0)),
    persistence: clamp01to100(m.persistence + (d.persistence ?? 0)),
    focus: clamp01to100(m.focus + (d.focus ?? 0)),
    gratitude: clamp01to100(m.gratitude + (d.gratitude ?? 0)),
  };
}

/** Auto-tick: vision items with a net-worth target flip to achieved once met;
 *  the effective quadrant is re-inferred. Keeps derived-ish flags consistent. */
function reconcile(state: LifeState): LifeState {
  const c = deriveLife(state);
  const visionBoard = state.visionBoard.map((v) => {
    if (v.achieved) return v;
    const met = v.condition
      ? isVisionAchieved(v.condition, state, c)
      : v.targetNetWorth != null
        ? c.netWorth >= v.targetNetWorth
        : false;
    return met ? { ...v, achieved: true } : v;
  });
  return { ...state, visionBoard, quadrant: c.suggestedQuadrant };
}

export function lifeReducer(state: LifeState, action: LifeAction): LifeState {
  switch (action.type) {
    case 'RESET':
      return action.state;
    case 'SET_AGE':
      return reconcile({ ...state, age: clampAge(action.age) });
    case 'ADVANCE_YEAR':
      return reconcile({ ...state, age: clampAge(state.age + 1) });
    case 'ADJUST_CASH':
      return reconcile({ ...state, cash: state.cash + action.delta });
    case 'SET_CASH':
      return reconcile({ ...state, cash: action.value });
    case 'SET_LIVING_COSTS':
      return reconcile({ ...state, livingCosts: Math.max(0, action.value) });
    case 'ADD_ASSET':
      return reconcile({ ...state, assets: [...state.assets, action.asset] });
    case 'BUY_ASSET':
      return reconcile({
        ...state,
        cash: state.cash - action.asset.value,
        assets: [...state.assets, action.asset],
      });
    case 'REMOVE_ASSET':
      return reconcile({ ...state, assets: state.assets.filter((a) => a.id !== action.id) });
    case 'ADD_LIABILITY':
      return reconcile({ ...state, liabilities: [...state.liabilities, action.liability] });
    case 'REMOVE_LIABILITY':
      return reconcile({ ...state, liabilities: state.liabilities.filter((l) => l.id !== action.id) });
    case 'ADJUST_MINDSET':
      return reconcile({ ...state, mindset: adjustMindset(state.mindset, action.deltas) });
    case 'SET_MINDSET':
      return reconcile({ ...state, mindset: action.mindset });
    case 'SET_QUADRANT':
      return { ...state, quadrant: action.quadrant }; // explicit override, no reconcile
    case 'ADD_VISION':
      return reconcile({ ...state, visionBoard: [...state.visionBoard, action.item] });
    case 'ACHIEVE_VISION':
      return {
        ...state,
        visionBoard: state.visionBoard.map((v) => (v.id === action.id ? { ...v, achieved: true } : v)),
      };
    case 'REMOVE_VISION':
      return { ...state, visionBoard: state.visionBoard.filter((v) => v.id !== action.id) };
    case 'ADD_MENTOR':
      if (state.mastermind.length >= 4) return state; // up to 4 slots
      return { ...state, mastermind: [...state.mastermind, action.mentor] };
    case 'REMOVE_MENTOR':
      return { ...state, mastermind: state.mastermind.filter((m) => m.id !== action.id) };
    case 'SET_HARVESTED_WEEK':
      return { ...state, lastHarvestedWeek: Math.max(state.lastHarvestedWeek ?? 0, action.week) };
    case 'MARK_VISION_PROMPTED':
      return { ...state, lastVisionBracket: Math.max(state.lastVisionBracket ?? 0, visionBracket(state.age)) };
    case 'END_OF_YEAR': {
      // A year of asset cashflow is realised into cash: 12 months of passive
      // income, minus the carrying costs of assets and liabilities. (Abstract
      // "living costs" stay out of the cash settle — they shape the Freedom Meter,
      // not a kid's literal bank balance.)
      const c = deriveLife(state);
      const liabilityCost = state.liabilities.reduce((a, l) => a + l.monthlyCost, 0);
      const assetCost = state.assets.reduce((a, x) => a + x.monthlyCost, 0);
      const annualNet = Math.round(12 * (c.passiveIncome - liabilityCost - assetCost));
      const next = reconcile({
        ...state,
        age: clampAge(state.age + 1),
        cash: state.cash + annualNet,
      });
      const nc = deriveLife(next);
      // Snowball watch: did the player put money to work this year (asset value
      // grew vs the year's baseline)? If yes, the idle clock resets; if cash is
      // still sitting idle, it ticks up another year.
      const IDLE_THRESHOLD = 50;
      const investedThisYear = nc.totalAssetValue > (state.yearStart?.totalAssetValue ?? 0);
      const cashIdleYears = investedThisYear
        ? 0
        : next.cash >= IDLE_THRESHOLD
          ? (state.cashIdleYears ?? 0) + 1
          : 0;
      return {
        ...next,
        cashIdleYears,
        yearStart: { wealth: nc.totalWealth, freedomPct: nc.freedomPct, totalAssetValue: nc.totalAssetValue },
      };
    }
    case 'DEPOSIT_TO_SAVINGS': {
      const SAVINGS_ID = 'savings-jar';
      const existing = state.assets.find((a) => a.id === SAVINGS_ID);
      const newValue = (existing?.value ?? 0) + Math.max(0, action.amount);
      const monthlyIncome = Math.round(newValue * action.monthlyRate);
      const savings: LifeAsset = existing
        ? { ...existing, value: newValue, monthlyIncome }
        : { id: SAVINGS_ID, type: 'savings', name: 'Savings', value: newValue, monthlyIncome, monthlyCost: 0 };
      const assets = existing
        ? state.assets.map((a) => (a.id === SAVINGS_ID ? savings : a))
        : [...state.assets, savings];
      return reconcile({ ...state, assets });
    }
    case 'MARK_KC_TRIGGERED': {
      const lp = state.learningProgress ?? { triggered: {}, answers: [] };
      if (lp.triggered[action.id]) return state; // idempotent
      return { ...state, learningProgress: { ...lp, triggered: { ...lp.triggered, [action.id]: true } } };
    }
    case 'RECORD_KC_ANSWER': {
      const lp = state.learningProgress ?? { triggered: {}, answers: [] };
      // Replace any existing answer with the same id, otherwise append.
      const idx = lp.answers.findIndex((a) => a.id === action.answer.id);
      const answers = idx >= 0 ? lp.answers.map((a, i) => (i === idx ? action.answer : a)) : [...lp.answers, action.answer];
      return { ...state, learningProgress: { ...lp, answers } };
    }
    case 'MARK_MENTOR_READ':
      return { ...state, lastMentorReadWeek: Math.max(state.lastMentorReadWeek ?? 0, action.week) };
    case 'MARK_FIRST_STAND_TAP':
      return state.firstStandTapped ? state : { ...state, firstStandTapped: true };
    case 'SET_PLAYER_NAME': {
      // First name only, trimmed, capped at 16 chars. An all-whitespace entry
      // is ignored (keeps any previous name rather than blanking it).
      const name = action.name.trim().slice(0, 16);
      return name ? { ...state, playerName: name } : state;
    }
    default:
      return state;
  }
}
